import type { IDataSource } from "@/data/dao/service/BaseServiceDao";
import type { ILinkableEntity, IMultiLanguageLinkableEntity } from "@/data/model/Shared";
import { Format } from "@/data/util/Format";

import { logError } from "@/shared/logging/errors";
import { Ripple } from "@/shared/ui/Ripple";

import { trans } from "@/operator/localization/Trans";

export interface ISelectValueFormatter<T> {
    (m: T): string;
}

export interface ISortValuesBy<T> {
    (a: T[]): T[];
}

export interface ISelectConfig<T> {
    options?: Array<T>;
    dataSource?: IDataSource<T>;
    isMultiSelect?: boolean;
    optionalText?: string;
    lazyFetch?: boolean;
    formatter?: ISelectValueFormatter<T | Array<T>>;
    sortBy?: ISortValuesBy<T>;
}

export class Select<T> {
    private static instanceIt = 0;
    private instance: number;
    private $element: JQuery;
    private $opener: JQuery;
    private $label: JQuery;

    public onChange?: { (newValue?: T | Array<T>): void };
    private cfg: ISelectConfig<T>;

    private current?: T | Array<T>;
    private options?: Array<T>;

    constructor(cfg?: ISelectConfig<T>) {
        Select.instanceIt++;
        this.instance = Select.instanceIt;

        this.cfg = { isMultiSelect: false, lazyFetch: false };
        this.cfg = { ...this.cfg, ...cfg };

        if (this.cfg.dataSource === undefined && this.cfg.options === undefined) {
            this.cfg.options = [];
            if (this.cfg.optionalText === undefined) this.cfg.optionalText = trans.settings.pleaseSelect + "...";
        }

        this.$element = $('<div class="select ins' + this.instance + '" />');
        this.$opener = $('<div class="opener" />').appendTo(this.$element);
        this.$label = $('<div class="lbl" />').appendTo(this.$opener);
        this.$opener.append('<i class="fas fa-caret-down"></i>');

        Ripple.bind(
            this.$opener.click(() => {
                if (this.$element.hasClass("open")) this.close();
                else this.open();
            })
        );

        if (this.cfg.options === undefined && this.cfg.dataSource !== undefined) {
            if (this.cfg.lazyFetch === false) {
                this.fetchOptions().catch((ex) => {
                    logError(ex);
                });
            }
        } else if (this.cfg.options !== undefined) this.options = this.cfg.options;
    }

    public setOptions(options: Array<T>) {
        this.current = undefined;
        this.options = options;

        if (this.options.length > 0) this.current = this.options[0];

        this.setLabelByOption(this.current);
    }

    public getValue(): T | undefined {
        if (this.cfg.isMultiSelect) return undefined;

        return this.current as T;
    }

    public setValue(val?: T | ILinkableEntity | IMultiLanguageLinkableEntity): Select<T> {
        if (val !== undefined) this.current = this.options!.find((c) => c["id"] === val!["id"]);
        else this.current = undefined;

        this.setLabelByOption(this.current);
        return this;
    }

    public setValues(vals: Array<T | ILinkableEntity | IMultiLanguageLinkableEntity>): Select<T> {
        if (vals !== undefined && vals.length !== 0) {
            if (this.options === undefined || this.options.length === 0) this.current = [];
            else this.current = this.options.filter((c) => vals.find((d) => d["id"] === c["id"]) !== undefined);
        } else this.current = [];

        this.setLabelByOption(this.current);

        return this;
    }

    public getValues(): Array<T> {
        if (!this.cfg.isMultiSelect) return [];

        const s = this.current as Array<T>;
        if (s === undefined) return [];

        return s;
    }

    public fetchOptions(abortSignal?: AbortSignal): Promise<void> {
        return new Promise((resolve) => {
            if (this.cfg.dataSource === undefined) {
                resolve();
                return;
            }

            return this.cfg.dataSource.getData({}, abortSignal).then((s) => {
                this.options = this.cfg.sortBy !== undefined ? this.cfg.sortBy(s.items) : s.items;

                //check for current element
                if (this.current !== undefined)
                    this.current = this.options.find((c) => c["id"] === this.current!["id"]);

                if (this.current === undefined && this.options.length !== 0 && this.cfg.optionalText === undefined)
                    this.current = this.cfg.isMultiSelect ? [this.options[0]] : this.options[0];

                this.setLabelByOption(this.current);

                resolve();
            });
        });
    }

    private open() {
        this.$element.addClass("open");
        this.$opener.find("> .fas").removeClass("fa-caret-down").addClass("fa-caret-up");
        const options = $('<div class="options scrollable" />').appendTo(this.$element);

        for (const i in this.options) {
            const opt = this.options[i];
            const name =
                this.cfg.formatter !== undefined
                    ? this.cfg.formatter(opt)
                    : opt["name"] === undefined
                      ? ""
                      : opt["name"];
            const option = $('<div class="option"><div class="value">' + Format.htmlEscape(name) + "</div></div>").data(
                "model",
                opt
            );
            if (this.cfg.isMultiSelect) option.prepend('<div class="check"></div>');

            if (this.current !== undefined) {
                if (!this.cfg.isMultiSelect && this.current!["id"] === opt["id"]) option.addClass("selected");
                else if (this.cfg.isMultiSelect && this.current!["find"]((d) => d["id"] === opt["id"]) !== undefined)
                    option.addClass("selected");
            }

            Ripple.bind(
                option.appendTo(options).click((e) => {
                    if (this.cfg.isMultiSelect) {
                        $(e.currentTarget).toggleClass("selected");
                        this.changeOption();
                    } else {
                        const existingSelected = this.$element.find("> .options > .option.selected");
                        if (existingSelected.length !== 0 && existingSelected.is($(e.currentTarget))) {
                            this.close();
                            return;
                        } else {
                            existingSelected.removeClass("selected");
                            $(e.currentTarget).addClass("selected");
                            this.changeOption();
                            this.close();
                        }
                    }
                })
            );
        }

        options[0].offsetHeight;
        options.addClass("shown");

        setTimeout(() => {
            $("html")
                .unbind(`click.select${this.instance}`)
                .bind(`click.select${this.instance}`, (e) => {
                    if (
                        $(e.target).is(this.$element) ||
                        $(e.target).parents(".select.ins" + this.instance).length !== 0
                    )
                        return;

                    this.close();
                });
        }, 200);
    }

    private close() {
        this.$element.removeClass("open");
        this.$opener.find("> .fas").removeClass("fa-caret-up").addClass("fa-caret-down");
        const opts = this.$element
            .find("> .options")
            .stop(true, false)
            .removeClass("shown")
            .delay(200)
            .queue(() => {
                opts.remove();
            });

        $("html").unbind(`click.select${this.instance}`);
    }

    private setLabelByOption(opt?: T | Array<T>) {
        if (opt === undefined || opt!["length"] == 0) {
            this.$label.html(
                this.cfg.optionalText !== undefined ? this.cfg.optionalText : trans.settings.pleaseSelect + "..."
            );
            return;
        }

        const val =
            this.cfg.formatter !== undefined ? this.cfg.formatter(opt) : opt!["name"] === undefined ? "" : opt!["name"];
        this.$label.html(Format.htmlEscape(val) ?? "");
    }

    private changeOption() {
        const selected = this.$element.find("> .options > .option.selected");
        if (this.cfg.isMultiSelect) {
            const newSelected = new Array<T>();
            selected.each((i, e) => {
                newSelected.push($(e).data("model"));
            });

            this.current = newSelected;
        } else {
            this.current = selected.length !== 0 ? selected.data("model") : undefined;
        }

        this.setLabelByOption(this.current);
        if (this.onChange !== undefined) this.onChange(this.current);
    }

    public setError(hasError: boolean): Select<T> {
        if (hasError) this.$element.addClass("haserror");
        else this.$element.removeClass("haserror");

        return this;
    }

    public appendTo($el: JQuery): Select<T> {
        this.$element.appendTo($el);

        return this;
    }
}
